import { createPath, useLocation } from '@remix-run/react';
import * as Sentry from '@sentry/browser';
import {
  generateV0Fp,
  initializeAnalytics,
  initializeFingerprintV3,
  setGlobalProperties,
  setUserProperties,
  useAnalyticsStore,
} from '@tectonic/analytics';
import { loadScript, usePrevious } from '@tectonic/utils';
import { isNil } from 'lodash-es';
import { useEffect } from 'react';
import { usePageDurationTracker } from './usePageDurationTracker';
import { usePageViewTracker } from './usePageViewTracker';
import useSessionDurationTracker from './useSessionDurationTracker';

import type { MixpanelConfig } from '@tectonic/analytics';
import type { User } from '@tectonic/types';

// Keeps the store for analytics in sync with ui state.
const useAnalyticsStoreEffect = () => {
  const setParams = useAnalyticsStore((state) => state.setParams);
  const location = useLocation();

  // There's no way to get previous url reliable. We use ref to remember previous
  // location and construct it manually when it is available.
  // https://stackoverflow.com/questions/3528324/how-to-get-the-previous-url-in-javascript
  const prevLocationRef = usePrevious<Location>(null);

  useEffect(() => {
    const previousUrl = prevLocationRef.current
      ? createPath(prevLocationRef.current ?? {})
      : null;
    // @ts-ignore
    prevLocationRef.current = location;
    // @ts-ignore
    setParams({ previousUrl });
  }, [prevLocationRef, location, setParams]);
};

const useAnalyticsInitEffect = (env: Record<string, string>) => {
  const {
    MIXPANEL_PROJECT_TOKEN,
    MIXPANEL_PROXY_DOMAIN,
    MIXPANEL_DEBUG_MODE,
    NODE_ENV,
  } = env;

  useEffect(() => {
    const mixpanel: MixpanelConfig = {
      token: MIXPANEL_PROJECT_TOKEN,
      options: {
        ignore_dnt: true,
        persistence: 'localStorage',
        debug: NODE_ENV !== 'production' || !!MIXPANEL_DEBUG_MODE,
        api_host: MIXPANEL_PROXY_DOMAIN,
      },
    };

    initializeAnalytics({ mixpanel });
  }, [
    MIXPANEL_PROJECT_TOKEN,
    MIXPANEL_PROXY_DOMAIN,
    MIXPANEL_DEBUG_MODE,
    NODE_ENV,
  ]);
};

// We have custom implementation of tracking. Therefore, we need to set, reset
// and alias user as per our backend implementation.
// 1. `ttUserId`
//    - We add `ttUserId` as current user id to identity a user for an event.
//    - It is always in sync with the user available in context.
// 2. `identify`
//    - We identify user only when the user signs in or signs up.
// 3. `aliases`
//    - Users are aliased when they login. We don't aliases a user when they sign
//      up cause the user id doesn't change.
const useAnalyticsUserEffect = (currentUser: User) => {
  const { externalUserId: userId, simpleId } = currentUser;
  const isAnonymous =
    isNil(currentUser?.isAnonymous) || currentUser.isAnonymous === true;

  // Keep `ttUserId` up to date.
  useEffect(() => {
    setGlobalProperties({
      ttUserId: userId,
      ttSourceUserId: simpleId,
    });
  }, [userId, simpleId]);

  // Keep `isAnonymous` up to date.
  useEffect(() => {
    Sentry.setUser({ id: userId });
    setUserProperties({ isAnonymous });
    setGlobalProperties({ isAnonymous });
  }, [isAnonymous, userId]);
};

const useGtagEffect = (env: Record<string, string>) => {
  const { GTAG_HOST, GTAG_ID, GTAG_JS_URL, ORGANIZATION } = env;

  useEffect(() => {
    loadScript(GTAG_JS_URL);
    globalThis.dataLayer = globalThis.dataLayer ?? [];
    globalThis.gtag = function gtagFn() {
      // eslint-disable-next-line prefer-rest-params
      globalThis.dataLayer.push(arguments);
    };
    globalThis.gtag('js', new Date());
    globalThis.gtag('config', GTAG_ID, {
      sever_container_url: `https://${GTAG_HOST}`,
      tt_event_source: 'HERMES_CLI',
      tt_org: ORGANIZATION,
      tt_ga_tag: GTAG_ID,
    });
  }, [GTAG_HOST, GTAG_ID, GTAG_JS_URL, ORGANIZATION]);
};

// TODO for testing storing the data in localstorage, will move to using react components + state, env, etc once finalized
const useClientFPEffect = (env: Record<string, string>) => {
  // const { ENABLE_FP_PRO, NODE_ENV } = env;

  // useEffect(() => {
  //   (async () => {
  //     if (NODE_ENV !== 'production' || ENABLE_FP_PRO !== 'true') {
  //       return
  //     }
  //     try {
  //       const fp = await initializeFingerprintPro();
  //       const fpData = await fp.get({ extendedResult: true });
  //       window.localStorage.setItem("fingerPrintPro", JSON.stringify(fpData))
  //     } catch (err) {
  //       // log to sentry
  //     }
  //   })();
  // }, [ENABLE_FP_PRO, NODE_ENV]);

  useEffect(() => {
    (async () => {
      try {
        const fp = await initializeFingerprintV3();
        const fpData = await fp.get();
        window.localStorage.setItem('fingerPrintV3', JSON.stringify(fpData));
      } catch (err) {
        // log to sentry
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const fp = await generateV0Fp();
        window.localStorage.setItem('ttClientFpV0', fp);
      } catch (err) {
        // log to sentry
      }
    })();
  }, []);
};

const useAnalyticsEffect = ({
  env,
  currentUser,
}: {
  env: Record<string, string>;
  currentUser: unknown;
}) => {
  useAnalyticsStoreEffect();
  useGtagEffect(env);
  useAnalyticsInitEffect(env);
  useAnalyticsUserEffect(currentUser as User);
  useClientFPEffect(env);
  usePageViewTracker();
  usePageDurationTracker();
  useSessionDurationTracker();
};

export { useAnalyticsEffect };
